/**
 * Versioned HTTP contract — talent `v1` — for the Talent Hub api
 * (`services/talent-hub-api`, mounted same-origin at `/api/talent/v1/*`).
 *
 * Same rules as `./v1.js`: both sides parse the SAME Zod shapes (ADR 0005); the
 * `{data, error, meta}` envelope + error kinds are reused verbatim; breaking changes
 * ship as `talent-v2` alongside, never by mutating these.
 *
 * The talent domain (`../talent/domain.js`) is plain types (contracts doctrine), so the
 * wire Zod schemas are defined here — they mirror those types exactly and are the single
 * parse boundary the web app and the api both use (real-first, ADR 0068).
 *
 * Venture scoping: the client supplies `ventureId` and the server scopes every row by it
 * (owner id is derived server-side from the Access identity / a single-owner stopgap).
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';

export const TALENT_HTTP_CONTRACT_VERSION = 'talent-v1' as const;

/** The talent enums, defined once so people + evidence schemas can never drift apart. */
const TALENT_ARCHETYPES = ['ceo', 'operations', 'engineering', 'science', 'product', 'commercial', 'clinical', 'domain-expert'] as const;
const TALENT_FIELDS = ['ai', 'climate', 'health', 'energy', 'defense', 'fintech', 'infra', 'product', 'materials', 'logistics', 'consumer'] as const;

const ProvenanceEvidenceSchema = z.object({
  label: z.string(),
  detail: z.string().optional(),
  url: z.string().optional(),
});

const FieldProvenanceSchema = z.object({
  sourceId: z.string(),
  sourceLabel: z.string(),
  method: z.enum(['measured', 'benchmark', 'modeled', 'estimated', 'llm-research', 'proxy']),
  confidence: z.enum(['low', 'med', 'high']),
  asOf: z.string(),
  evidence: z.array(ProvenanceEvidenceSchema),
  note: z.string().optional(),
});

const PersonNameSchema = z.object({ en: z.string(), ar: z.string().optional() });

const PersonSourceSchema = z.object({
  provider: z.enum(['linkedin', 'github', 'paper', 'org-chart', 'crunchbase', 'news', 'referral', 'manual']),
  externalId: z.string().optional(),
  url: z.string().optional(),
  provenance: FieldProvenanceSchema,
});

const TalentSignalSchema = z.object({
  id: z.string(),
  kind: z.enum(['funding', 'publication', 'departure', 'launch', 'available', 'award', 'move', 'other']),
  summary: z.string(),
  detectedAt: z.number(),
  url: z.string().optional(),
  provenance: FieldProvenanceSchema,
});

const TalentRelationshipSchema = z.object({
  strength: z.number(),
  band: z.enum(['inner-circle', 'ally', 'warm', 'cold']),
  lastContactAt: z.number().optional(),
  warmIntroPersonIds: z.array(z.string()),
});

const TouchPointSchema = z.object({
  id: z.string(),
  kind: z.enum(['note', 'meeting', 'coffee', 'event', 'intro']),
  at: z.number(),
  note: z.string(),
  actor: z.string(),
});

/** Wire shape of a tracked {@link ../talent/domain.js#Person}. Parsed on both sides. */
export const TalentPersonSchema = z.object({
  id: z.string(),
  ventureId: z.string(),
  name: PersonNameSchema,
  archetype: z.enum(TALENT_ARCHETYPES),
  title: z.string(),
  company: z.string(),
  fields: z.array(z.enum(TALENT_FIELDS)),
  seniority: z.enum(['senior', 'staff', 'principal', 'director', 'exec']),
  location: z.string().optional(),
  sources: z.array(PersonSourceSchema),
  relationship: TalentRelationshipSchema,
  signals: z.array(TalentSignalSchema),
  touchPoints: z.array(TouchPointSchema),
  tracked: z.boolean(),
});

/** POST `/api/talent/v1/people/list` — all tracked people for a venture. */
export const ListPeopleRequestSchema = z.object({ ventureId: z.string().min(1) });
export const ListPeopleResponseSchema = apiEnvelope(z.array(TalentPersonSchema));

/** POST `/api/talent/v1/people/get` — one tracked person by id, venture-scoped. */
export const GetPersonRequestSchema = z.object({ ventureId: z.string().min(1), personId: z.string().min(1) });
export const GetPersonResponseSchema = apiEnvelope(TalentPersonSchema);

/** POST `/api/talent/v1/ingest/github` — run a GitHub search, persist matched people. */
export const IngestGithubRequestSchema = z.object({ ventureId: z.string().min(1), query: z.string().min(1) });
export const IngestGithubResponseSchema = apiEnvelope(z.object({ added: z.number(), people: z.array(TalentPersonSchema) }));

/**
 * POST `/api/talent/v1/people/create` — persist hand-entered / CSV-imported people (idempotent by
 * id). The client builds the full {@link TalentPersonSchema} (with `manual` provenance, ADR 0070)
 * and the server upserts + returns the persisted rows, owner-scoped server-side.
 */
export const CreatePeopleRequestSchema = z.object({ people: z.array(TalentPersonSchema).min(1) });
export const CreatePeopleResponseSchema = apiEnvelope(z.array(TalentPersonSchema));

/** One analogous-company benchmark (wire mirror of {@link ../talent/domain.js#RoleBenchmark}). */
const RoleBenchmarkSchema = z.object({
  company: z.string(),
  note: z.string(),
  url: z.string().optional(),
});

/** Wire mirror of {@link ../talent/domain.js#RoleEvidence} — why a venture needs a role (ADR 0075 P14). */
export const RoleEvidenceSchema = z.object({
  role: z.enum(TALENT_ARCHETYPES),
  rationale: z.string(),
  benchmarks: z.array(RoleBenchmarkSchema),
  provenance: FieldProvenanceSchema,
});

/**
 * POST `/api/talent/v1/evidence/derive` — the gateway-LLM evidence upgrade (ADR 0075 P14b). The
 * client sends the active venture's context + the role; the server returns LLM-derived evidence
 * (`method:'llm-research'`) when a key is configured, else `evidence:null` + `backend:'stub'` so the
 * client falls back to its local heuristic (no silent failure; never fabricated fact, ADR 0068).
 */
export const DeriveEvidenceRequestSchema = z.object({
  ventureId: z.string().min(1),
  title: z.string(),
  thesis: z.string(),
  fields: z.array(z.enum(TALENT_FIELDS)),
  role: z.enum(TALENT_ARCHETYPES),
});
export const DeriveEvidenceResponseSchema = apiEnvelope(
  z.object({ evidence: RoleEvidenceSchema.nullable(), backend: z.enum(['live', 'stub']) }),
);

/* ── Scout (the "scout bay", backlog N10 / S5) ──────────────────────────────────────────────
 * A scout launches a background browser run on the agent-runner (behind Cloudflare Access) and
 * folds its cited findings into a shortlist. Two routes: start (returns a runId) + poll (status +
 * candidates). Live only when `AGENT_RUNNER_URL` + a CF Access service token are configured, else
 * `backend:'stub'` (the browser adapter shows its honest "not connected"). Candidates are always
 * source-cited + honestly caveated — never fabricated fact (ADR 0068). */
const SCOUT_SENIORITIES = ['any', 'senior', 'staff', 'principal', 'director', 'exec'] as const;

/** What the operator is scouting for (wire mirror of talent-hub `core/scout.ts#ScoutTarget`). */
export const ScoutTargetSchema = z.object({
  role: z.enum(TALENT_ARCHETYPES),
  fields: z.array(z.enum(TALENT_FIELDS)),
  seniority: z.enum(SCOUT_SENIORITIES),
  keywords: z.string().max(400),
});

/** One scouted person — always source-cited + honestly caveated. */
export const ScoutCandidateSchema = z.object({
  name: z.string().min(1).max(200),
  headline: z.string().max(400),
  sourceUrl: z.string().max(2000),
  note: z.string().max(400),
});

/** The scout run's lifecycle as the browser sees it (maps the agent-runner statuses down). */
export const ScoutStatusSchema = z.enum(['running', 'done', 'error']);

/** POST `/api/talent/v1/scout` — start a scout; returns a runId (live) or backend:'stub'. */
export const ScoutStartRequestSchema = z.object({ ownerId: z.string().min(1), target: ScoutTargetSchema });
export const ScoutStartResponseSchema = apiEnvelope(
  z.object({ runId: z.string().nullable(), backend: z.enum(['live', 'stub']) }),
);

/** POST `/api/talent/v1/scout/poll` — poll a running scout for its status + any candidates so far. */
export const ScoutPollRequestSchema = z.object({ ownerId: z.string().min(1), runId: z.string().min(1) });
export const ScoutPollResponseSchema = apiEnvelope(
  z.object({
    status: ScoutStatusSchema,
    candidates: z.array(ScoutCandidateSchema),
    /** Set when `status:'error'` — a human-readable, non-sensitive cause. */
    message: z.string().nullable(),
  }),
);

export type ScoutTargetWire = z.infer<typeof ScoutTargetSchema>;
export type ScoutCandidateWire = z.infer<typeof ScoutCandidateSchema>;
